import { useState } from 'react';
import PropTypes from 'prop-types';

import noop from 'lodash/fp/noop';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import Carousel from 'Components/Carousel';
import { Button } from 'Components/LegacyButton';
import TileImageHoverable from 'Components/TileImageHoverable';
import { Typography } from 'Components/Typography';

import content from 'assets/content/socialImpact';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const GridSection = styled.section`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: auto;
  align-items: center;
  width: 100%;
  background-color: ${theme.colors.neutral[900]};
  padding: 0.5px 0;
  grid-gap: 0.5px;

  ${legacyTheme.breakpoints.up('md')} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const GridTitle = styled.div`
  grid-column: 1 / 3;

  color: ${theme.colors.primary[400]};
  background-color: ${theme.colors.neutral[100]};
  height: 100%;
  max-height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;

  padding-left: 15%;
  ${legacyTheme.breakpoints.match('md')} {
    padding: 40px 0 40px 30px;
  }
`;

const Title = styled(Typography)`
  font-size: 16px;
`;

const Subtitle = styled(Typography)`
  font-size: 24px;
  max-width: 320px;

  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 46px;
    max-width: 600px;
  }
`;

const CarouselWrapper = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  flex: 1;

  top: 50%;
  left: 50%;
  z-index: 9999;

  width: 90%;

  transform: translate(-50%, -50%);
`;

const CarouselCloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 99999;

  width: 15px;
  height: 15px;
  padding: 0;

  appearance: none;
  background: none;
  border: none;

  cursor: pointer;

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;

    border-bottom: 2px solid white;
    transform: rotate(45deg);
    content: '';
  }

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;

    border-bottom: 2px solid white;
    transform: rotate(-45deg);
    content: '';
  }
`;

const HoveredPartnerContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${theme.colors.secondary[200]};
  justify-content: center;
  align-items: center;
  padding: 0 32px;
`;

const PartnerDescription = styled(Typography)`
  padding: 16px 0;
  ${legacyTheme.breakpoints.match('lg')} {
    ${legacyTheme.breakpoints.up('md')} {
      padding: 8px 0;
      font-size: 12px;
    }
  }
`;

const CarouselContentWrapper = styled.div`
  position: relative;
  height: 60vh;
`;

const HoveredPartnerContent = ({ name, description, href, dataClick, dataFrom }) => (
  <HoveredPartnerContentContainer>
    <Typography align="center" bold color="noir" upperCase variant="mono1">
      {name}
    </Typography>
    <PartnerDescription align="center" color="noir" variant="p1">
      {description}
    </PartnerDescription>
    <div>
      <Button
        Component="a"
        data-click={dataClick}
        data-from={dataFrom}
        href={href}
        noMargin
        target="_blank"
        variant="underlinedBlack"
      >
        Learn more
      </Button>
    </div>
  </HoveredPartnerContentContainer>
);

HoveredPartnerContent.propTypes = {
  dataFrom: PropTypes.string.isRequired,
  dataClick: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
};

const LocalPartnersBlock = () => {
  const { isMobile } = useResponsiveVariant();
  const [clickedStory, setClickedStory] = useState(undefined);

  return (
    <>
      <GridSection>
        <GridTitle>
          <Title align="left" markupName="h4" paragraph variant="mono1">
            {content.socialPartnersBlock.title}
          </Title>

          <Subtitle align="left" markupName="h5" variant="mono1">
            {content.socialPartnersBlock.content}
          </Subtitle>
        </GridTitle>
        {content.socialPartnersBlock.partners.map((partner, index) => (
          <TileImageHoverable
            key={partner.href}
            backgroundImageSize={40}
            dataClick={partner.name}
            dataFrom={partner.dataFrom}
            image={partner.image}
            isMobile={isMobile}
            onClickTile={isMobile ? () => setClickedStory(index) : noop}
          >
            <HoveredPartnerContent
              dataClick={partner.dataClick}
              dataFrom={content.socialPartnersBlock.dataFrom}
              description={partner.description}
              href={partner.href}
              name={partner.name}
            />
          </TileImageHoverable>
        ))}
      </GridSection>
      {isMobile && clickedStory !== undefined && (
        <CarouselWrapper>
          <CarouselCloseButton
            aria-label="close"
            onClick={() => setClickedStory(undefined)}
            onKeyDown={() => setClickedStory(undefined)}
            type="button"
          />

          <Carousel isInfinite isLooping step={clickedStory} whiteControls>
            {content.socialPartnersBlock.partners.map(partner => (
              <CarouselContentWrapper key={partner.href}>
                <HoveredPartnerContent
                  dataClick={partner.dataClick}
                  description={partner.description}
                  href={partner.href}
                  name={partner.name}
                />
              </CarouselContentWrapper>
            ))}
          </Carousel>
        </CarouselWrapper>
      )}
    </>
  );
};

export default LocalPartnersBlock;
